import React, { useState, useEffect } from "react"
import { FormControl, MenuItem, Select, Slider, Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton } from "@mui/material";
import { Settings as SettingsIcon } from '@mui/icons-material';
import { useTranslation } from "react-i18next";
import { GetWatermarkSettings, SaveWatermarkSettings } from "../../../Services/Watermark";

const AddWaterMark: React.FunctionComponent<{ onEnhancedImage: any }> = (props) => {

    const { t } = useTranslation();
    const [selectPosition, setSelectPosition] = useState<any>("");
    const [isHovered, setIsHovered] = useState<boolean>(false);
    const [selectWatermark, setSelectWatermark] = useState<any>([]);
    const [transparent, setTransparent] = useState<any>();
    const [hasSettings, setHasSettings] = useState<boolean>(false);
    const [isCheckingSettings, setIsCheckingSettings] = useState<boolean>(true);
    const [settingsData, setSettingsData] = useState<any>(null);
    const [showConfigDialog, setShowConfigDialog] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [watermarkApplied, setWatermarkApplied] = useState<boolean>(false);
    const [showSettingsPopup, setShowSettingsPopup] = useState<boolean>(false);

    useEffect(() => {
        checkWatermarkSettings();
    }, []);

    // Auto-close popup when watermark is applied with existing settings
    useEffect(() => {
        if (hasSettings && watermarkApplied) {
            // Trigger a custom event to close the popup
            const event = new CustomEvent('watermarkApplied', { detail: { applied: true } });
            window.dispatchEvent(event);
        }
    }, [hasSettings, watermarkApplied]);

    const checkWatermarkSettings = async () => {
        try {
            setIsCheckingSettings(true);
            const settings = await GetWatermarkSettings();
            if (settings && settings.watermarkLogo && settings.position && settings.transparency !== undefined) {
                setHasSettings(true);
                setSettingsData(settings);
                // Auto-apply watermark with saved settings
                applyWatermarkWithSettings(settings);
            } else {
                setHasSettings(false);
            }
        } catch (error: any) {
            console.log("No watermark settings found:", error.message);
            setHasSettings(false);
        } finally {
            setIsCheckingSettings(false);
        }
    };

    const applyWatermarkWithSettings = (settings: any) => {
        const enhanceObj = {
            "id": "",
            "watermark": settings.watermarkLogo ? [{ id: 1, src: settings.watermarkLogo, ext: "png" }] : [],
            "position": settings.position,
            "transparent": settings.transparency
        };
        props.onEnhancedImage(enhanceObj);
        setWatermarkApplied(true);
    };

    const openSettingsPopup = () => {
        // Load current settings into form
        if (settingsData) {
            setSelectWatermark(settingsData.watermarkLogo ? [{ id: 1, src: settingsData.watermarkLogo, ext: "png" }] : []);
            setSelectPosition(settingsData.position);
            setTransparent(settingsData.transparency);
        }
        setShowSettingsPopup(true);
    };

    const handleSaveAndApply = async () => {
        if (!selectWatermark.length || !selectPosition) {
            return;
        }

        try {
            setIsSaving(true);
            // Save settings to backend
            await SaveWatermarkSettings({
                watermarkLogo: selectWatermark[0]?.src,
                position: selectPosition,
                transparency: transparent || 0,
            });

            // Apply watermark
            const enhanceObj = {
                "id": "",
                "watermark": selectWatermark,
                "position": selectPosition,
                "transparent": transparent
            };
            props.onEnhancedImage(enhanceObj);

            // Close dialogs and update state
            setShowConfigDialog(false);
            setShowSettingsPopup(false);
            setHasSettings(true);
            setSettingsData({
                watermarkLogo: selectWatermark[0]?.src,
                position: selectPosition,
                transparency: transparent || 0
            });
        } catch (error: any) {
            console.error("Error saving watermark settings:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const HandleChangeWatermark = (event: any) => {
        for (let i = 0; i < event.target.files.length; i++) {
            const reader = new FileReader();
            reader.readAsDataURL(event.target.files[i]);
            reader.onload = () => {
                const base_64 = reader.result as string;
                const ext = event.target.files[i].type.split('/');
                let image = {
                    "id": i + 1,
                    "src": base_64,
                    "ext": ext[1]
                }
                setSelectWatermark((prevSelectedImages: any) =>
                    [...prevSelectedImages, image]
                );
            };
        }
    }

    const handleDeleteWatermark = (idToDelete: any) => {
        const updatedImages = selectWatermark.filter((image: any) => image.id !== idToDelete);
        setSelectWatermark(updatedImages);
    };

    const handleChangeTransparent = (event: any) => {
        setTransparent(event.target.value)
    }

    const handleSelectPosition = (event: any) => {
        setSelectPosition(event.target.value)
    }

    const Enhance = () => {
        const enhanceObj = {
            "id": "",
            "watermark": selectWatermark,
            "position": selectPosition,
            "transparent": transparent
        }
        props.onEnhancedImage(enhanceObj);
    };

    if (isCheckingSettings) {
        return (
            <div className="card border-20">
                <div className="padd-20">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                        <div>Checking watermark settings...</div>
                    </div>
                </div>
            </div>
        );
    }

    if (hasSettings && watermarkApplied) {
        // Don't show any popup if settings are configured and watermark is applied
        return null;
    }

    return (
        <>
            {!showConfigDialog ?
                <div className="card border-20">
                    <div className="padd-20">
                        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
                            <div className="text-center">
                                <div className="mb-3">
                                    <i className="fa-solid fa-image" style={{ fontSize: "48px", color: "#6366F1" }}></i>
                                </div>
                                <h5 className="mb-3">Configure Watermark</h5>
                                <p className="text-muted mb-3">
                                    No watermark settings found. Click below to configure your watermark.<br />
                                    You can also change these settings later from Settings → Company Profile → Watermark Settings.
                                </p>
                                <button
                                    type="button"
                                    className="button-dash back-746AFF color-FFFFFF border-746AFF padd-15-30"
                                    onClick={() => setShowConfigDialog(true)}
                                >
                                    Configure Watermark
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                :
                <div className="padd-20">
                    <div className="row mb-4">
                        <label className="title-20 bold-700 mb-3 color-black" htmlFor="Upload">{t("settings.watermark")}</label>
                        {selectWatermark.length == 0 ?
                            <div className="upload-xml">
                                <label className="form_container_1 padd-15" id="upload-container">
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.3332 2.66699H11.9998C5.33317 2.66699 2.6665 5.33366 2.6665 12.0003V20.0003C2.6665 26.667 5.33317 29.3337 11.9998 29.3337H19.9998C26.6665 29.3337 29.3332 26.667 29.3332 20.0003V13.3337" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <g opacity="0.4">
                                            <path d="M23.9995 10.666V2.66602L26.6662 5.33268" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M23.9997 2.66602L21.333 5.33268" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </g>
                                        <path opacity="0.4" d="M3.56006 25.2661L10.1334 20.8528C11.1867 20.1461 12.7067 20.2261 13.6534 21.0394L14.0934 21.4261C15.1334 22.3194 16.8134 22.3194 17.8534 21.4261L23.4001 16.6661C24.4401 15.7728 26.1201 15.7728 27.1601 16.6661L29.3334 18.5328" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p>{t("addWatermark.uploadWatermark")}</p>
                                    <input className="form__file" id="upload-files" accept='image/*' type="file" onChange={HandleChangeWatermark} />
                                </label>
                                <div id="files-list-container"></div>
                            </div> :
                            <div id="read-watermark" onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}>
                                {selectWatermark && selectWatermark.map((image: any, index: any) => {
                                    return <div key={index} style={{ position: "relative", display: "inline-block" }}>
                                        <img src={image.src} className="previewImage" id={`previewImage-${index}`} style={{ width: "190px", marginLeft: "0px" }} key={index} />
                                        <button
                                            style={{ position: "absolute", top: "5px", right: "25px", borderRadius: "50px", color: "#716BE3", border: "2px solid #716BE3", background: "#FFF" }}
                                            onClick={() => handleDeleteWatermark(image.id)}
                                        >
                                            <i className="fa-solid fa-xmark"></i>
                                        </button>
                                    </div>
                                })}
                            </div>}
                    </div>

                    <FormControl fullWidth>
                        <Select
                            className="custom-select mb-4"
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select' }}
                            value={selectPosition || ""}
                            onChange={handleSelectPosition}
                            sx={{
                                border: "1px solid #746AFF",
                                background: "#FFFFFF !important",
                                color: "#746AFF",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#746AFF"
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#746AFF"
                                }
                            }}
                            MenuProps={{
                                PaperProps: {
                                    sx: {
                                        backgroundColor: "#FFFFFF"
                                    }
                                }
                            }}
                        >
                            <MenuItem value="" disabled> {t("addWatermark.selectPosition")} </MenuItem>
                            <MenuItem value={"Top Left"}>{t("addWatermark.topLeft")}</MenuItem>
                            <MenuItem value={"Top Right"}>{t("addWatermark.topRight")}</MenuItem>
                            <MenuItem value={"Center"}>{t("addWatermark.center")}</MenuItem>
                            <MenuItem value={"Bottom Left"}>{t("addWatermark.bottomLeft")}</MenuItem>
                            <MenuItem value={"Bottom Right"}>{t("addWatermark.bottomRight")}</MenuItem>
                        </Select>
                    </FormControl>

                    <label className="form-label  mb-3" htmlFor="selectTypeOpt">{t("addWatermark.transparency")}</label>
                    <div className="d-flex align-items-center mb-4">
                        <span className="para-14 bold-500 font-Inter color-344054">0%</span>
                        <Slider aria-label="Image Score" sx={{ color: "#6366F1", marginRight: "15px", marginLeft: "15px", height: "8px", borderRadius: "8px" }} value={transparent || 0} onChange={handleChangeTransparent} valueLabelDisplay="auto" max={100} min={0} />
                        <span className="para-14 bold-500 font-Inter color-344054">100%</span>
                    </div>
                    <div className="d-flex justify-content-end">
                        <button type="button" className="button-dash back-trans color-746AFF border-746AFF padd-15 marg-right-10" onClick={() => setShowConfigDialog(false)}>
                            Cancel
                        </button>
                        <div className="d-flex" style={{ gap: '0px' }}>
                            <button type="button" className="button-dash back-746AFF color-FFFFFF border-746AFF padd-15"
                                style={{ borderTopRightRadius: '0', borderBottomRightRadius: '0', borderRight: '1px solid rgba(255,255,255,0.3)' }}
                                onClick={handleSaveAndApply}
                                disabled={isSaving || !selectWatermark.length || !selectPosition}
                            >
                                {isSaving ? "Saving..." : "Save & Apply"}
                            </button>
                            <button type="button" 
                                className="button-dash back-746AFF color-FFFFFF border-746AFF"
                                style={{ borderTopLeftRadius: '0', borderBottomLeftRadius: '0', padding: '10px 15px' }}
                                onClick={openSettingsPopup}
                                disabled={isSaving}
                                title="Settings"
                            >
                                <SettingsIcon fontSize="small" />
                            </button>
                        </div>
                    </div>
                </div>
            }

            {/* Settings Popup Dialog */}
            <Dialog 
                open={showSettingsPopup} 
                onClose={() => setShowSettingsPopup(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Watermark Settings
                </DialogTitle>
                <DialogContent>
                    <div className="mt-3">
                        <label className="title-16 bold-600 mb-3 color-black" htmlFor="Upload">{t("settings.watermark")}</label>
                        {selectWatermark.length == 0 ?
                            <div className="upload-xml mb-3">
                                <label className="form_container_1 padd-15" id="upload-container">
                                    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.3332 2.66699H11.9998C5.33317 2.66699 2.6665 5.33366 2.6665 12.0003V20.0003C2.6665 26.667 5.33317 29.3337 11.9998 29.3337H19.9998C26.6665 29.3337 29.3332 26.667 29.3332 20.0003V13.3337" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <g opacity="0.4">
                                            <path d="M23.9995 10.666V2.66602L26.6662 5.33268" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M23.9997 2.66602L21.333 5.33268" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        </g>
                                        <path opacity="0.4" d="M3.56006 25.2661L10.1334 20.8528C11.1867 20.1461 12.7067 20.2261 13.6534 21.0394L14.0934 21.4261C15.1334 22.3194 16.8134 22.3194 17.8534 21.4261L23.4001 16.6661C24.4401 15.7728 26.1201 15.7728 27.1601 16.6661L29.3334 18.5328" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                    <p>{t("addWatermark.uploadWatermark")}</p>
                                    <input className="form__file" id="upload-files-popup" accept='image/*' type="file" onChange={HandleChangeWatermark} />
                                </label>
                            </div> :
                            <div className="mb-3" onMouseEnter={() => setIsHovered(true)}
                                onMouseLeave={() => setIsHovered(false)}>
                                {selectWatermark && selectWatermark.map((image: any, index: any) => {
                                    return <div key={index} style={{ position: "relative", display: "inline-block" }}>
                                        <img src={image.src} className="previewImage" id={`previewImage-popup-${index}`} style={{ width: "190px", marginLeft: "0px" }} key={index} />
                                        <button
                                            style={{ position: "absolute", top: "5px", right: "25px", borderRadius: "50px", color: "#716BE3", border: "2px solid #716BE3", background: "#FFF" }}
                                            onClick={() => handleDeleteWatermark(image.id)}
                                        >
                                            <i className="fa-solid fa-xmark"></i>
                                        </button>
                                    </div>
                                })}
                            </div>}

                        <FormControl fullWidth className="mb-3">
                            <Select
                                className="custom-select"
                                displayEmpty
                                inputProps={{ 'aria-label': 'Select' }}
                                value={selectPosition || ""}
                                onChange={handleSelectPosition}
                            >
                                <MenuItem value="" disabled> {t("addWatermark.selectPosition")} </MenuItem>
                                <MenuItem value={"Top Left"}>{t("addWatermark.topLeft")}</MenuItem>
                                <MenuItem value={"Top Right"}>{t("addWatermark.topRight")}</MenuItem>
                                <MenuItem value={"Center"}>{t("addWatermark.center")}</MenuItem>
                                <MenuItem value={"Bottom Left"}>{t("addWatermark.bottomLeft")}</MenuItem>
                                <MenuItem value={"Bottom Right"}>{t("addWatermark.bottomRight")}</MenuItem>
                            </Select>
                        </FormControl>

                        <label className="form-label mb-3" htmlFor="selectTypeOpt">{t("addWatermark.transparency")}</label>
                        <div className="d-flex align-items-center mb-3">
                            <span className="para-14 bold-500 font-Inter color-344054">0%</span>
                            <Slider aria-label="Image Score" sx={{ color: "#6366F1", marginRight: "15px", marginLeft: "15px", height: "8px", borderRadius: "8px" }} value={transparent || 0} onChange={handleChangeTransparent} valueLabelDisplay="auto" max={100} min={0} />
                            <span className="para-14 bold-500 font-Inter color-344054">100%</span>
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <button type="button" className="button-dash back-trans color-746AFF border-746AFF padd-15 marg-right-10" onClick={() => setShowSettingsPopup(false)}>
                        Cancel
                    </button>
                    <button type="button" className="button-dash back-746AFF color-FFFFFF border-746AFF padd-15"
                        onClick={handleSaveAndApply}
                        disabled={isSaving || !selectWatermark.length || !selectPosition}
                    >
                        {isSaving ? "Saving..." : "Save & Apply"}
                    </button>
                </DialogActions>
            </Dialog>
        </>
    )
}
export default AddWaterMark;